import {
  AdminSpinner,
  AdvancedQueryTypes,
  BestPerformingComponent,
  BestPerformingLocationsEntity,
  Div,
  getDashboardWorstPerformingLocationsFilterDeliveries,
  GetDashboardWorstPerformingLocationsFilterParams,
  lightTheme,
  ModalPortal,
  PaginationParams,
  Button,
  getTimeZone,
  P,
  reportError,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

async function getWorstPerformingLocationsFilter(
  page: number,
  worstPerforming: BestPerformingLocationsEntity[] | null,
  setWorstPerforming: Dispatch<
    SetStateAction<BestPerformingLocationsEntity[] | null>
  >,
  hasMore: boolean,
  setHasMore: Dispatch<SetStateAction<boolean>>,
  setPage: Dispatch<SetStateAction<number>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  type: AdvancedQueryTypes,
  selectedLocationId?: string,
  selectedBrandId?: string,
  brandTemplateId?: string,
  from?: string | Date,
  to?: string | Date,
  setIsOpen?: Dispatch<SetStateAction<boolean>>
) {
  if (!hasMore) return;
  setLoading(true);

  const LIMIT = 10;

  const params: PaginationParams &
    GetDashboardWorstPerformingLocationsFilterParams = {
    type,
    page,
    limit: LIMIT,
    from:
      DateTime.fromISO(from as string)
        .startOf('day')
        .toISODate() || '',
    to:
      DateTime.fromISO(to as string)
        .endOf('day')
        .toISODate() || '',
    timeZone: getTimeZone,
  };

  if (
    type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_ANY_BRAND ||
    type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_V_GRUBS ||
    type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITH_V_GRUBS ||
    type === AdvancedQueryTypes.LOCATION_SPECIFIC_ONLY_V_GRUBS
  ) {
    params.locationIds = [selectedLocationId!];
  }
  if (type === AdvancedQueryTypes.V_GRUBS_BRAND_SPECIFIC) {
    params.brandTemplateIds = [brandTemplateId!];
  }
  if (type === AdvancedQueryTypes.LOCATION_BRAND_SPECIFIC) {
    params.locationIds = [selectedLocationId!];
    if (selectedBrandId) {
      params.brandIds = [selectedBrandId!];
    }
  }

  try {
    const { data } = await getDashboardWorstPerformingLocationsFilterDeliveries(
      params
    );

    if (worstPerforming === null) {
      setIsOpen?.(true);
      setWorstPerforming(data.data);
    } else {
      setWorstPerforming((prev) => [...(prev || []), ...data.data]);
    }

    setHasMore(data.data.length === LIMIT);
    setPage(page + 1);
  } catch (error) {
    reportError(error);
  } finally {
    setLoading(false);
  }
}

export const WorstPerformingLocationsFilterModal = ({
  title,
  vDrive = false,
  type,
  selectedLocationId,
  selectedBrandId,
  brandTemplateId,
  from,
  to,
}: {
  title: string;
  vDrive?: boolean;
  type: AdvancedQueryTypes;
  selectedLocationId?: string;
  selectedBrandId?: string;
  brandTemplateId?: string;
  from?: string | Date;
  to?: string | Date;
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [worstPerforming, setWorstPerforming] = useState<
    BestPerformingLocationsEntity[] | null
  >(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [ref, inView] = useInView();

  useEffect(() => {
    if (inView && hasMore && worstPerforming !== null) {
      getWorstPerformingLocationsFilter(
        page,
        worstPerforming,
        setWorstPerforming,
        hasMore,
        setHasMore,
        setPage,
        setLoadingMore,
        type,
        selectedLocationId,
        selectedBrandId,
        brandTemplateId,
        from,
        to,
        setIsOpen
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const handleOpenModal = () => {
    setPage(1);
    setHasMore(true);
    getWorstPerformingLocationsFilter(
      1,
      null,
      setWorstPerforming,
      true,
      setHasMore,
      setPage,
      setLoading,
      type,
      selectedLocationId,
      selectedBrandId,
      brandTemplateId,
      from,
      to,
      setIsOpen
    );
  };

  return (
    <>
      {loading && <AdminSpinner small overflowBorderRadius="30px" />}
      <Div $flexNone $mt="10px" $mr="32">
        <Button $wSM="110px" $w="130px" $small onClick={handleOpenModal}>
          See Locations
        </Button>
      </Div>
      <ModalPortal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="500px"
        maxHeight="calc(65px * 10)"
        modalOverflow="hidden"
        titleWithExit={title}
      >
        {loadingMore && <AdminSpinner small absolute />}

        {worstPerforming?.map((item, index) => (
          <BestPerformingComponent
            key={index}
            item={item}
            index={index}
            vDrive={vDrive}
            title={title}
            valueColor={lightTheme.primary}
            noTotalSales
          />
        ))}
        {worstPerforming?.length === 0 && (
          <Div $height="65px" $flex $alignCenter $center>
            <P $m="0">No Data Available.</P>
          </Div>
        )}
        <Div $height="20px" ref={ref}></Div>
      </ModalPortal>
    </>
  );
};
